import { z } from 'zod';

import { DashboardWidgetWidth } from '@/components/dashboard/model';
import {
  dashboardDateScopeSchema,
  DashboardMetric,
  DashboardDateScopeMode,
} from '../../dashboard/model';

export enum PublisherDashboardWidgetType {
  Metric = 'PUBLISHER_METRIC',
  PerformanceChart = 'PUBLISHER_PERFORMANCE_CHART',
  Inventory = 'PUBLISHER_INVENTORY',
}

const base = {
  id: z.string().trim().min(1),
  type: z.enum(PublisherDashboardWidgetType),
  width: z.enum(DashboardWidgetWidth),
  dateScope: dashboardDateScopeSchema,
};

const publisherMetricSchema = z.enum([
  DashboardMetric.AdClicks,
  DashboardMetric.Rpm,
  DashboardMetric.AdRequests,
  DashboardMetric.AdImpressions,
  DashboardMetric.Revenue,
]);

export const publisherMetricWidgetSchema = z.object({
  ...base,
  type: z.literal(PublisherDashboardWidgetType.Metric),
  metric: publisherMetricSchema,
});

export const publisherPerformanceChartWidgetSchema = z.object({
  ...base,
  type: z.literal(PublisherDashboardWidgetType.PerformanceChart),
  metrics: z.array(publisherMetricSchema).min(1),
});

export const publisherInventoryWidgetSchema = z.object({
  ...base,
  type: z.literal(PublisherDashboardWidgetType.Inventory),
  pageSize: z.number().int().positive().max(100),
});

export const publisherDashboardWidgetSchema = z.discriminatedUnion('type', [
  publisherMetricWidgetSchema,
  publisherPerformanceChartWidgetSchema,
  publisherInventoryWidgetSchema,
]);

export const publisherDashboardDocumentSchema = z.object({
  version: z.literal(1),
  widgets: z.array(publisherDashboardWidgetSchema).superRefine((widgets, context) => {
    const ids = new Set<string>();
    widgets.forEach((widget, index) => {
      if (ids.has(widget.id)) {
        context.addIssue({
          code: 'custom',
          message: 'Publisher dashboard widget IDs must be unique.',
          path: [index, 'id'],
        });
      }
      ids.add(widget.id);
    });
  }),
});

export const publisherDashboardInheritedDateScope = {
  mode: DashboardDateScopeMode.Inherit,
} as const;

export type PublisherDashboardWidget = z.infer<typeof publisherDashboardWidgetSchema>;
